/**
 * TrophyTokensVideoInline — iter87.533 (user-requested)
 *
 * Native, embedded (non-modal) player for the Help & FAQ entry — sits
 * at the top of "How do Trophy Tokens work?", above the text summary.
 * `preload="metadata"` + a poster frame mean the real video bytes
 * aren't fetched until the reader actually taps play — no autoplay.
 */
import React from "react";
import { Play } from "lucide-react";
import { TROPHY_TOKENS_VIDEO_SRC, TROPHY_TOKENS_VIDEO_POSTER } from "./TrophyTokensVideoModal";

export default function TrophyTokensVideoInline() {
    const videoRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    // iter87.534 (testing-agent-reported) — native `controls` rendered
    // unconditionally meant the browser's own play button + scrubber sat
    // underneath our big custom play circle before first play, showing
    // two play affordances stacked on the poster at once. Only show the
    // native bar once playback has actually started at least once; the
    // custom overlay is the only play control until then.
    const [started, setStarted] = React.useState(false);

    return (
        <div
            className="relative w-full rounded-lg overflow-hidden border border-sky-400/40 bg-black mb-3"
            style={{ aspectRatio: "16 / 9" }}
            data-testid="trophy-tokens-faq-video-player"
        >
            <video
                ref={videoRef}
                className="w-full h-full object-cover"
                controls={started}
                playsInline
                preload="metadata"
                poster={TROPHY_TOKENS_VIDEO_POSTER}
                aria-label="How Trophy Tokens work, 90 second walkthrough"
                onPlay={() => { setPlaying(true); setStarted(true); }}
                onPause={() => setPlaying(false)}
                data-testid="trophy-tokens-faq-video-element"
            >
                <source src={TROPHY_TOKENS_VIDEO_SRC} type="video/mp4" />
                Your browser doesn't support embedded video — you can still{" "}
                <a href={TROPHY_TOKENS_VIDEO_SRC} className="text-sky-400 underline">
                    download the clip
                </a>
                .
            </video>
            {!started && (
                <button
                    type="button"
                    onClick={() => videoRef.current?.play()}
                    aria-label="Play the Trophy Tokens walkthrough video"
                    data-testid="trophy-tokens-faq-video-play-overlay"
                    className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/25 transition-colors group"
                >
                    <span className="w-14 h-14 rounded-full bg-sky-400 flex items-center justify-center border-2 border-black shadow-[0_0_24px_rgba(56,189,248,0.6)] group-hover:scale-105 transition-transform">
                        <Play className="w-6 h-6 text-black ml-1" fill="currentColor" />
                    </span>
                </button>
            )}
        </div>
    );
}
